import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { DigitalQuestion, PartialBy, QuestionCaseSetting } from '../../../redux/types';
import ContextQuestionEditSettings, { MutableQuestionCaseSetting } from './ContextQuestionEditSettings';
import SurveyOptionContext from '../../../views/admin/SurveyOptionContext';

type Props = {
  selectedSectionId?: number;
  contextQuestions: DigitalQuestion[];
  settingsByCtxQuestionsId: Record<number, QuestionCaseSetting>;
  addQuestionCaseSetting: (qcs: Omit<QuestionCaseSetting, 'id' | 'digitalCaseId'>) => void;
  updateQuestionCaseSetting: (qcs: Omit<QuestionCaseSetting, 'digitalCaseId'>) => void;
};

const ContextQuestionSettings: React.FC<Props> = ({
  selectedSectionId,
  contextQuestions,
  settingsByCtxQuestionsId,
  addQuestionCaseSetting,
  updateQuestionCaseSetting,
}: Props) => {
  const [settingEditMode, setSettingEditMode] = useState<boolean>(false);
  const [settingsBufferMap, setSettingsBufferMap] = useState<Record<number, PartialBy<MutableQuestionCaseSetting, 'id' | 'digitalCaseId'>>>();
  const { disableEdit } = useContext(SurveyOptionContext);

  useEffect(() => {
    const newMap = {
      ...settingsByCtxQuestionsId,
    };
    setSettingsBufferMap(newMap);
  }, [settingsByCtxQuestionsId]);

  if (!selectedSectionId
    || !contextQuestions.length) {
    return null;
  }

  const saveSettingsHandler = () => {
    if (!settingsBufferMap) {
      setSettingEditMode(false);
      return;
    }
    const changedSettings = Object.values(settingsBufferMap).filter((s) => s.changed).map((s) => {
      const { changed, ...rest } = s;
      return rest as PartialBy<QuestionCaseSetting, 'id' | 'digitalCaseId'>;
    });
    changedSettings.forEach((s) => {
      if (!s.id) {
        addQuestionCaseSetting(s);
      } else if (s.id !== undefined) {
        updateQuestionCaseSetting(s as Omit<QuestionCaseSetting, 'digitalCaseId'>);
      }
    });
    setSettingEditMode(false);
  };

  const cancelSettingsHandler = () => {
    setSettingsBufferMap(settingsByCtxQuestionsId);
    setSettingEditMode(false);
  };

  return (
    <Box>
      <Box display="flex">
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Methodical Questions Settings</Typography>
        {
          !settingEditMode && (
            <Tooltip title="Bulk Settings" placement="top">
              <>
                <IconButton
                  disabled={disableEdit}
                  onClick={() => setSettingEditMode(true)}
                >
                  <EditIcon />
                </IconButton>
              </>
            </Tooltip>
          )
        }
        {
          settingEditMode && (
            <>
              <Tooltip title="Save Settings" placement="top">
                <IconButton onClick={saveSettingsHandler}>
                  <SaveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel Changes" placement="top">
                <IconButton onClick={cancelSettingsHandler}>
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            </>
          )
        }
      </Box>
      {
        !settingEditMode && (
          <List>
            {
              contextQuestions.map((cq) => (
                <ListItem
                  key={cq.id}
                  disablePadding
                  secondaryAction={(
                    <Box display="flex" gap={1} alignItems="center">
                      <Chip label={cq.type} />
                      <Chip
                        label={<FormattedMessage id="questioneditor.required" />}
                        icon={settingsByCtxQuestionsId[cq.id] && settingsByCtxQuestionsId[cq.id].compulsory ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
                        color={settingsByCtxQuestionsId[cq.id] && settingsByCtxQuestionsId[cq.id].compulsory ? 'primary' : 'secondary'}
                        variant="outlined"
                      />
                    </Box>
                  )}
                >
                  <ListItemButton>
                    <ListItemText primary={cq.text} />
                  </ListItemButton>
                </ListItem>
              ))
            }
          </List>
        )
      }
      {
        settingEditMode && (
          <ContextQuestionEditSettings
            selectedSectionId={selectedSectionId}
            contextQuestions={contextQuestions}
            settingsByCtxQuestionsId={settingsBufferMap}
            onChangeSettings={setSettingsBufferMap}
          />
        )
      }
    </Box>
  );
};

export default ContextQuestionSettings;
